import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  DraggableAttributes,
  DraggableSyntheticListeners,
} from '@dnd-kit/core';
import { ReactComponent as IncidentIoIcon } from '@elementary/common-ui/assets/16x16/incident-io.svg';
import { ReactComponent as RootlyIcon } from '@elementary/common-ui/assets/16x16/rootly.svg';
import { ReactComponent as OpsgenieIcon } from '@elementary/common-ui/assets/16x16/opsgenie.svg';
import { ReactComponent as PagerIcon } from '@elementary/common-ui/assets/16x16/pager.svg';
import { ReactComponent as SlackIcon } from '@elementary/common-ui/assets/16x16/slack.svg';
import { ReactComponent as TeamsIcon } from '@elementary/common-ui/assets/16x16/teams.svg';
import { ReactComponent as EmailIcon } from '@elementary/common-ui/assets/16x16/email.svg';
import {
  Accordion as AccordionBase,
  AccordionDetails as AccordionDetailsBase,
  AccordionSummary as AccordionSummaryBase,
  FilterChip,
  Flex,
  FlexCenter,
  OverflowTooltip,
} from '@elementary/common-ui/components';
import useAccount from '@elementary/common-ui/hooks/useAccount';
import useEnvPermission from '@elementary/common-ui/hooks/useEnvPermission';
import { EnvironmentPermission } from '@elementary/common-ui/types';
import { ColorToken } from '@elementary/common-ui/style/types';
import {
  getColor,
  space,
  radius,
  fontSize,
  fontWeight,
} from '@elementary/common-ui/style/utils';
import { DragIndicator } from '@mui/icons-material';
import { Tooltip, Typography } from '@mui/material';
import { AlertCategory, AlertFilterModel, IntegrationType } from 'shared/types';
import RuleMenu from './RuleMenu';

export type RuleAccordionProps = {
  id: string;
  name: string;
  channels: { name: string; type: IntegrationType }[];
  categories: Array<AlertCategory>;
  assetFilters?: Array<AlertFilterModel>;
  incidentFilters?: Array<AlertFilterModel>;
  active: boolean;
  onEdit: () => void;
  onActivate: (id: string) => void;
  onDeactivate: (id: string) => void;
  onDelete: (id: string) => void;
  editPermission: EnvironmentPermission;
  index?: number;
  isDefault?: boolean;
  categoriesGatedByCloudTests?: boolean;
  cron?: string;
};

export type RuleReorderProps = {
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  dragHandle?: {
    attributes: DraggableAttributes;
    listeners: DraggableSyntheticListeners;
    setActivatorNodeRef: (element: HTMLElement | null) => void;
  };
  isDragging?: boolean;
};

type Props = RuleAccordionProps & RuleReorderProps;

const categoriesMap: Record<AlertCategory, string> = {
  automated_volume_anomalies: 'Automated volume anomalies',
  automated_freshness_anomalies: 'Automated freshness anomalies',
  metric_anomalies: 'Metric anomalies',
  pipeline_performance: 'Pipeline task performance',
  custom_sql_tests: 'Custom SQL tests',
  dbt_generic_tests: 'dbt tests',
  dbt_source_freshness: 'dbt source freshness',
  model_build: 'Model errors',
  python_sdk: 'Python SDK tests',
  pipeline: 'Job failures',
};

const channelIconMap: Record<IntegrationType, JSX.Element> = {
  slack: <SlackIcon />,
  pager_duty: <PagerIcon />,
  microsoft_teams: <TeamsIcon />,
  microsoft_teams_bot: <TeamsIcon />,
  opsgenie: <OpsgenieIcon />,
  incident_io: <IncidentIoIcon />,
  rootly: <RootlyIcon />,
  email: <EmailIcon />,
};

const Accordion = styled(AccordionBase)`
  &:hover {
    cursor: pointer;
  }
`;

const DRAG_HANDLE_OFFSET = 28;

const AccordionSummary = styled(AccordionSummaryBase)<{
  $hasDragHandle: boolean;
}>`
  overflow: hidden;

  &&.MuiAccordionSummary-root {
    padding-left: ${({ $hasDragHandle }) =>
      24 + ($hasDragHandle ? DRAG_HANDLE_OFFSET : 0)}px;
  }

  &:hover {
    background-color: unset;
  }

  .MuiAccordionSummary-content {
    justify-content: space-between;
    align-items: center;
  }

  &.MuiAccordionSummary-root.Mui-expanded {
    border-bottom: none;
  }
`;

const AccordionDetails = styled(AccordionDetailsBase)<{
  $hasIndex: boolean;
  $hasDragHandle: boolean;
}>`
  display: flex;
  flex-direction: column;
  max-width: 100%;
  gap: ${space('lg')};
  padding-top: 0;
  border-top: none;
  padding-left: ${({ $hasIndex, $hasDragHandle }) =>
    ($hasIndex ? 62 : 24) + ($hasDragHandle ? DRAG_HANDLE_OFFSET : 0)}px;
  background-color: unset;
`;

const DragHandle = styled(FlexCenter)`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  border-radius: ${radius('sm')};
  color: ${({ theme }) => theme.palette.content.secondary};
  cursor: grab;
  touch-action: none;

  &:hover,
  &:focus-visible {
    color: ${({ theme }) => theme.palette.content.primary};
    background-color: ${({ theme }) => theme.palette.surface.light};
  }

  &:active {
    cursor: grabbing;
  }

  &[aria-disabled='true'] {
    cursor: not-allowed;
    opacity: 0.5;
  }

  svg {
    font-size: ${fontSize('xl')};
  }
`;

const IdBox = styled(FlexCenter)`
  position: absolute;
  left: 0;
  top: 0;
  background: ${({ theme }) => theme.palette.surface.light};
  border-radius: ${radius(0, 0, 'lg', 0)};
  padding: ${space('xs')};
  width: 24px;
  height: 24px;
`;

const Section = styled(Flex)`
  gap: ${space('sm')};
  max-width: 100%;
  overflow: hidden;
`;

const NameSection = styled(Section)`
  max-width: 881px;
  flex-direction: column;
`;

const DetailSection = styled(Section)`
  gap: ${space('sm')};
  align-items: center;
  align-self: stretch;
  flex-wrap: wrap;
`;

const SectionTitle = styled(Typography)`
  font-size: ${fontSize('base')};
  color: ${({ theme }) => theme.palette.content.secondary};
  white-space: nowrap;
`;

const statusColorMap: Record<'active' | 'inactive', ColorToken> = {
  active: 'status.success.main',
  inactive: 'content.disabled',
};

const StatusChip = styled(FlexCenter)<{ $active: boolean }>`
  font-size: ${fontSize('xxs')};
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
  border-radius: ${radius('capsule')};
  padding: ${space('xxs', 'smMinus')};
  text-transform: uppercase;
  border: 1px solid
    ${({ theme, $active }) =>
      getColor(theme, statusColorMap[$active ? 'active' : 'inactive'])};
  color: ${({ theme, $active }) =>
    getColor(theme, statusColorMap[$active ? 'active' : 'inactive'])};
`;

const FiltersSection = ({
  title,
  filters,
}: {
  title: string;
  filters?: Array<AlertFilterModel>;
}) => (
  <DetailSection>
    <SectionTitle>{`${title}:`}</SectionTitle>
    {(filters || []).length > 0 ? (
      filters?.map((filter, i) => (
        <FlexCenter key={i} sx={{ gap: 1 }}>
          {i !== 0 && (
            <Typography
              sx={{
                fontSize: fontSize('sm'),
                fontWeight: fontWeight('semibold'),
                color: 'content.secondary',
              }}
            >
              AND
            </Typography>
          )}
          <FilterChip
            property={filter.property}
            relationship={filter.operator}
            value={filter.values}
          />
        </FlexCenter>
      ))
    ) : (
      <Typography sx={{ fontSize: fontSize('base') }}>---</Typography>
    )}
  </DetailSection>
);

const formatChannelName = (channel: string) => {
  try {
    const parsed = JSON.parse(channel);
    if (Array.isArray(parsed.to)) return parsed.to.join(', ');
    if (typeof parsed.to === 'string') return parsed.to;
    return JSON.stringify(parsed);
  } catch {
    return `#${channel}`;
  }
};

const RuleAccordion = ({
  id,
  name,
  channels,
  categories,
  assetFilters,
  incidentFilters,
  active,
  onEdit,
  onActivate,
  onDeactivate,
  onDelete,
  editPermission,
  index,
  isDefault,
  categoriesGatedByCloudTests,
  cron,
  onMoveUp,
  onMoveDown,
  dragHandle,
  isDragging,
}: Props) => {
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (isDragging) setIsExpanded(false);
  }, [isDragging]);
  const hasPermission = useEnvPermission();
  const { data: account } = useAccount();

  const cloudTestsEnabled = useMemo(
    () => account?.cloud_tests_enabled,
    [account],
  );

  const showCategories =
    categories.length > 0 &&
    (!categoriesGatedByCloudTests || cloudTestsEnabled);

  const handleMenuItemClick = (val: string) => {
    switch (val) {
      case 'edit':
        return onEdit();
      case 'deactivate':
        return onDeactivate(id);
      case 'activate':
        return onActivate(id);
      case 'delete':
        return onDelete(id);
      case 'move-up':
        return onMoveUp?.();
      case 'move-down':
        return onMoveDown?.();
      default:
        return null;
    }
  };

  return (
    <Accordion
      expanded={isExpanded}
      onChange={(_, expanded) => setIsExpanded(expanded)}
    >
      <AccordionSummary $hasDragHandle={!!dragHandle}>
        {dragHandle && (
          <DragHandle
            ref={dragHandle.setActivatorNodeRef}
            {...dragHandle.attributes}
            {...dragHandle.listeners}
            aria-label={`Reorder ${name}`}
            onClick={(e) => e.stopPropagation()}
          >
            <DragIndicator />
          </DragHandle>
        )}

        {index != null && (
          <Tooltip
            title={`Only the first rule in this list that matches an alert\nsends it. A broad rule near the top catches every alert it\nmatches, so rules below it never see those alerts.\nA meta.channel override in YAML only applies when the\ndefault rule is above the other matching rules.`}
            componentsProps={{
              tooltip: { sx: { whiteSpace: 'pre-wrap' } },
            }}
          >
            <IdBox>
              <Typography sx={{ fontSize: fontSize('xxs') }}>
                #
                <Typography
                  component="span"
                  sx={{
                    fontWeight: fontWeight('semibold'),
                    fontSize: fontSize('sm'),
                  }}
                >
                  {index}
                </Typography>
              </Typography>
            </IdBox>
          </Tooltip>
        )}

        <Flex sx={{ alignItems: 'center', gap: 1, minWidth: 0 }}>
          <NameSection>
            <Flex sx={{ alignItems: 'center', gap: 1 }}>
              <OverflowTooltip bold sx={{ width: 'unset !important' }}>
                {name}
              </OverflowTooltip>
              {isDefault && (
                <Typography
                  sx={{
                    color: 'content.secondary',
                    fontSize: fontSize('base'),
                  }}
                >
                  (Default)
                </Typography>
              )}
            </Flex>
            <Flex sx={{ flexWrap: 'wrap', rowGap: 1, columnGap: 3 }}>
              {channels.map((c, i) => (
                <Flex
                  key={`${c.name}.${i}`}
                  sx={{ gap: 1, alignItems: 'center', maxWidth: '100%' }}
                >
                  <Flex>{channelIconMap[c.type]}</Flex>
                  {c.name && (
                    <OverflowTooltip sx={{ fontSize: fontSize('base') }}>
                      {formatChannelName(c.name)}
                    </OverflowTooltip>
                  )}
                </Flex>
              ))}
            </Flex>
          </NameSection>
        </Flex>

        <Flex sx={{ alignItems: 'center', gap: 1 }}>
          {cron && (
            <Tooltip title="Digest cadence" placement="top">
              <Typography
                sx={{
                  fontSize: fontSize('sm'),
                  color: 'content.secondary',
                  whiteSpace: 'nowrap',
                }}
              >
                {cron}
              </Typography>
            </Tooltip>
          )}

          <StatusChip $active={!!active}>
            {active ? 'active' : 'inactive'}
          </StatusChip>

          {hasPermission(editPermission) && (
            <div onClick={(e) => e.stopPropagation()}>
              <RuleMenu
                onChange={handleMenuItemClick}
                isActive={active}
                isDefault={isDefault}
                isReorderable={!!dragHandle}
                canMoveUp={!!onMoveUp}
                canMoveDown={!!onMoveDown}
              />
            </div>
          )}
        </Flex>
      </AccordionSummary>

      <AccordionDetails $hasIndex={index != null} $hasDragHandle={!!dragHandle}>
        {showCategories && (
          <DetailSection sx={{ minWidth: 'fit-content' }}>
            <SectionTitle>Test categories:</SectionTitle>
            <Flex
              sx={{ gap: 2, flexWrap: 'wrap', alignItems: 'center' }}
              divider={
                <Typography sx={{ fontSize: fontSize('base') }}>|</Typography>
              }
            >
              {categories.map((category) => (
                <Typography key={category} sx={{ fontSize: fontSize('base') }}>
                  {categoriesMap[category]}
                </Typography>
              ))}
            </Flex>
          </DetailSection>
        )}
        <FiltersSection title="Incident filters" filters={incidentFilters} />
        <FiltersSection
          title="Impacted assets filters"
          filters={assetFilters}
        />
      </AccordionDetails>
    </Accordion>
  );
};

export default RuleAccordion;
